import React, { useState } from 'react';

import { Col, Row } from 'antd/lib/grid';
import {
    Button, Space, Select, Card,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getCore } from 'cvat-core-wrapper';
import { useSelector } from 'react-redux';
import { CombinedState } from 'reducers';

function DownloadPageComponent(): JSX.Element {
    const user = useSelector((state: CombinedState) => state.auth.user);
    const [downloading, setDownloading] = useState(false);
    const [downloadingAnnotations, setDownloadingAnnotations] = useState(false);
    const [downloadingSessions, setDownloadingSessions] = useState(false);
    const [downloadingProfiles, setDownloadingProfiles] = useState(false);
    const [timeRange, setTimeRange] = useState('7d');

    const handleDownloadStatusCSV = async (): Promise<void> => {
        try {
            setDownloading(true);
            const cvat = getCore();
            const blob = await cvat.server.downloadPlatformStatusCSV();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'platform_status_ERCVATBWVPublic.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download platform status CSV:', error);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadAnnotatorProfilesCSV = async (): Promise<void> => {
        try {
            setDownloadingProfiles(true);
            const cvat = getCore();
            const blob = await cvat.server.downloadAnnotatorProfilesCSV();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'annotator_profiles.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download annotator profiles CSV:', error);
        } finally {
            setDownloadingProfiles(false);
        }
    };

    const handleDownloadAnnotationsOutput = async (): Promise<void> => {
        try {
            setDownloadingAnnotations(true);
            const cvat = getCore();
            const blob = await cvat.server.downloadAnnotationsOutput();

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'annotations_output.tar.gz');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download annotations output:', error);
        } finally {
            setDownloadingAnnotations(false);
        }
    };

    const handleDownloadJobSessionsCSV = async (): Promise<void> => {
        try {
            setDownloadingSessions(true);
            const cvat = getCore();
            const blob = await cvat.server.downloadJobSessionsCSV(timeRange);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `job_sessions_${timeRange}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download job sessions CSV:', error);
        } finally {
            setDownloadingSessions(false);
        }
    };

    if (!user?.isStaff) {
        return (
            <Row justify='center' align='middle' style={{ height: '100%' }}>
                <Col>
                    <p>You do not have permission to view this page.</p>
                </Col>
            </Row>
        );
    }

    return (
        <Row style={{ marginTop: 16, padding: '0 24px' }}>
            <Col span={24}>
                <Space size='middle' direction='vertical'>
                    <Space size='middle' wrap>
                        <Button
                            type='primary'
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadStatusCSV}
                            loading={downloading}
                        >
                            Download Platform Status CSV
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadAnnotatorProfilesCSV}
                            loading={downloadingProfiles}
                        >
                            Download Annotator Profiles CSV
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleDownloadAnnotationsOutput}
                            loading={downloadingAnnotations}
                        >
                            Download Annotations Output
                        </Button>
                    </Space>
                    <Card
                        title='Job Sessions Telemetry'
                        size='small'
                        style={{ marginTop: 24, maxWidth: 500 }}
                    >
                        <Space size='middle'>
                            <Select
                                value={timeRange}
                                style={{ width: 140 }}
                                onChange={(value) => setTimeRange(value)}
                            >
                                <Select.Option value='7d'>Last 7 Days</Select.Option>
                                <Select.Option value='30d'>Last 30 Days</Select.Option>
                                <Select.Option value='all'>All Time</Select.Option>
                            </Select>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadJobSessionsCSV}
                                loading={downloadingSessions}
                            >
                                Download Job Sessions CSV
                            </Button>
                        </Space>
                    </Card>
                </Space>
            </Col>
        </Row>
    );
}

export default React.memo(DownloadPageComponent);
